import React, { useState } from 'react';
import { ChromePicker } from 'react-color';
import { LycraColorConfig, DEFAULT_LYCRA_COLORS } from '@/common/model/LycraColorConfig';
import { Button, Input } from '@/components/ui';

interface Props {
  config: LycraColorConfig[];
  onChange: (config: LycraColorConfig[]) => void;
  onSave: () => void;
  isSaving?: boolean;
}

export const LycraColorConfigEditor: React.FC<Props> = ({ config, onChange, onSave, isSaving }) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showAddColorPicker, setShowAddColorPicker] = useState(false);
  const [newColor, setNewColor] = useState({ color: '#000000', name: '' });
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const items = Array.from(config);
    const [draggedItem] = items.splice(draggedIndex, 1);
    items.splice(index, 0, draggedItem);

    // Update sort orders
    const updatedItems = items.map((item, idx) => ({
      ...item,
      sortOrder: idx + 1
    }));

    onChange(updatedItems);
    setDraggedIndex(index);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Add new color
  const addColor = () => {
    if (!newColor.name.trim() || !newColor.color) return;

    const updated = [
      ...config,
      {
        color: newColor.color.toUpperCase(),
        name: newColor.name.trim(),
        sortOrder: config.length + 1
      }
    ];
    onChange(updated);
    setNewColor({ color: '#000000', name: '' });
    setShowAddColorPicker(false);
  };

  // Remove color
  const removeColor = (index: number) => {
    const updated = config
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, sortOrder: i + 1 }));
    onChange(updated);
  };

  // Update existing color
  const updateColor = (index: number, updates: Partial<LycraColorConfig>) => {
    const updated = config.map((item, i) =>
      i === index ? { ...item, ...updates } : item
    );
    onChange(updated);
  };

  // Move color up
  const moveUp = (index: number) => {
    if (index === 0) return;
    const items = Array.from(config);
    [items[index - 1], items[index]] = [items[index], items[index - 1]];
    const updatedItems = items.map((item, idx) => ({
      ...item,
      sortOrder: idx + 1
    }));
    onChange(updatedItems);
  };

  // Move color down
  const moveDown = (index: number) => {
    if (index === config.length - 1) return;
    const items = Array.from(config);
    [items[index], items[index + 1]] = [items[index + 1], items[index]];
    const updatedItems = items.map((item, idx) => ({
      ...item,
      sortOrder: idx + 1
    }));
    onChange(updatedItems);
  };

  // Reset to defaults
  const resetToDefaults = () => {
    onChange([...DEFAULT_LYCRA_COLORS]);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Lycra Color Configuration</h3>
          <p className="text-sm text-gray-500">Drag to reorder. Top color = highest priority in results.</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={resetToDefaults}
        >
          Reset to Defaults
        </Button>
      </div>

      {/* Color list */}
      <div className="space-y-2 mb-4">
        {config.map((item, index) => (
          <div
            key={`${item.color}-${index}`}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            className={`flex items-center gap-2 p-2 bg-gray-50 rounded border ${
              draggedIndex === index ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
            } cursor-move hover:bg-gray-100`}
          >
            {/* Drag handle */}
            <span className="text-gray-400 cursor-grab">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"/>
              </svg>
            </span>

            {/* Sort order */}
            <span className="w-6 text-center text-sm font-medium text-gray-500">
              {item.sortOrder}
            </span>

            {/* Color swatch */}
            <div className="relative">
              <div
                className="w-8 h-8 rounded border border-gray-300 cursor-pointer shadow-sm"
                style={{ backgroundColor: item.color }}
                onClick={() => setEditingIndex(editingIndex === index ? null : index)}
              />
              {editingIndex === index && (
                <div className={`absolute z-50 left-0 ${index >= config.length - 3 ? 'bottom-10' : 'top-10'}`}>
                  <div className="fixed inset-0" onClick={() => setEditingIndex(null)} />
                  <div className="relative bg-white rounded-lg shadow-xl p-2 border border-gray-200">
                    <ChromePicker
                      color={item.color}
                      onChangeComplete={(color) => updateColor(index, { color: color.hex.toUpperCase() })}
                    />
                    <Button
                      size="sm"
                      className="mt-2 w-full"
                      onClick={() => setEditingIndex(null)}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Color name */}
            <Input
              type="text"
              value={item.name}
              onChange={(e) => updateColor(index, { name: e.target.value })}
              className="flex-1 h-8 text-sm"
              placeholder="Color name"
            />

            {/* Hex code */}
            <Input
              type="text"
              value={item.color}
              onChange={(e) => {
                let value = e.target.value.toUpperCase();
                if (!value.startsWith('#')) value = '#' + value;
                updateColor(index, { color: value });
              }}
              className="w-24 h-8 text-sm font-mono"
              placeholder="#FFFFFF"
            />

            {/* Move buttons */}
            <div className="flex flex-col">
              <button
                onClick={() => moveUp(index)}
                disabled={index === 0}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <button
                onClick={() => moveDown(index)}
                disabled={index === config.length - 1}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Remove button */}
            <button
              onClick={() => removeColor(index)}
              className="text-red-400 hover:text-red-600 p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {/* Add new color section */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div
              className="w-8 h-8 rounded border border-gray-300 cursor-pointer shadow-sm"
              style={{ backgroundColor: newColor.color }}
              onClick={() => setShowAddColorPicker(!showAddColorPicker)}
            />
            {showAddColorPicker && (
              <div className="absolute z-50 bottom-10 left-0">
                <div className="fixed inset-0" onClick={() => setShowAddColorPicker(false)} />
                <div className="relative bg-white rounded-lg shadow-xl p-2 border border-gray-200">
                  <ChromePicker
                    color={newColor.color}
                    onChangeComplete={(color) => setNewColor({ ...newColor, color: color.hex.toUpperCase() })}
                  />
                  <Button
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => setShowAddColorPicker(false)}
                  >
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Input
            type="text"
            placeholder="Color name"
            value={newColor.name}
            onChange={(e) => setNewColor({ ...newColor, name: e.target.value })}
            className="flex-1 h-8 text-sm"
          />

          <Input
            type="text"
            placeholder="#FFFFFF"
            value={newColor.color}
            onChange={(e) => {
              let value = e.target.value.toUpperCase();
              if (!value.startsWith('#')) value = '#' + value;
              setNewColor({ ...newColor, color: value });
            }}
            className="w-24 h-8 text-sm font-mono"
          />

          <Button onClick={addColor} size="sm" disabled={!newColor.name.trim()}>
            Add Color
          </Button>
        </div>
      </div>

      {/* Save button */}
      <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
        <Button
          onClick={onSave}
          disabled={isSaving}
          className="bg-green-600 hover:bg-green-700"
        >
          {isSaving ? 'Saving...' : 'Save Color Configuration'}
        </Button>
      </div>
    </div>
  );
};

export default LycraColorConfigEditor;
